import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { CreditCard, Lock, FileText, Wallet, Check } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const PAYMENT_OPTIONS = [
  { id: "retainer", label: "Retainer", desc: "Fund your case retainer", icon: Wallet },
  { id: "bill", label: "Pay a Bill", desc: "Settle an existing invoice", icon: FileText },
];

const PaymentDialog = () => {
  const { toast } = useToast();
  const [paymentType, setPaymentType] = useState("retainer");
  const [amount, setAmount] = useState("");
  const [name, setName] = useState("");

  const handlePay = (e) => {
    e.preventDefault();
    if (!amount || !name) {
      toast({ title: "Please enter your name and an amount", variant: "destructive" });
      return;
    }
    // MOCK payment — no real transaction is processed
    toast({
      title: "Payment received (demo)",
      description: `Thank you, ${name}. A $${amount} ${paymentType} payment was recorded.`,
    });
    setAmount("");
    setName("");
    setPaymentType("retainer");
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-[#c8a15a] text-[#c8a15a] hover:bg-[#c8a15a] hover:text-[#14213d] font-semibold bg-transparent"
        >
          Make a Payment
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#14213d] flex items-center gap-2">
            <CreditCard size={22} className="text-[#c8a15a]" />
            Make a Payment
          </DialogTitle>
          <DialogDescription>
            Securely submit a payment to the office. This is a demo form.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handlePay} className="space-y-4 pt-2">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Payment Type</label>
            <div className="grid grid-cols-2 gap-3">
              {PAYMENT_OPTIONS.map((opt) => {
                const active = paymentType === opt.id;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPaymentType(opt.id)}
                    className={`relative text-left rounded-xl border-2 p-4 transition-all duration-200 ${
                      active
                        ? "border-[#c8a15a] bg-[#c8a15a]/10 shadow-sm"
                        : "border-slate-200 bg-white hover:border-[#c8a15a]/50"
                    }`}
                  >
                    {active && (
                      <span className="absolute top-2 right-2 flex items-center justify-center w-5 h-5 rounded-full bg-[#c8a15a] text-[#14213d]">
                        <Check size={13} strokeWidth={3} />
                      </span>
                    )}
                    <span
                      className={`flex items-center justify-center w-10 h-10 rounded-lg mb-2.5 transition-colors ${
                        active ? "bg-[#14213d] text-[#c8a15a]" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      <Icon size={20} />
                    </span>
                    <span className="block font-semibold text-[#14213d]">{opt.label}</span>
                    <span className="block text-xs text-slate-500 mt-0.5">{opt.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Name on Account</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="h-11" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Amount (USD)</label>
            <Input
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="h-11"
            />
          </div>
          <Button type="submit" className="w-full bg-[#c8a15a] hover:bg-[#b58f45] text-[#14213d] font-semibold py-6 gap-2">
            <Lock size={16} /> Submit Payment
          </Button>
          <p className="text-xs text-slate-400 text-center">No real payment is processed in this demo.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentDialog;
